import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Loader2 } from 'lucide-react';
import { AppLayout } from 'src/components/layout/app-layout';
import { PortalDsAlert } from 'src/components/portal-design-system';
import { portalDs, PortalDsSectionHeader } from 'src/components/portal-design-system';
import { useSetPageTitle } from 'src/hooks/useSetPageTitle';
import { cn } from 'src/lib/utils';
import { ReduxState } from 'src/redux/redux-state';
import { getIsUserStaff } from 'src/redux/user/user.selectors';
import { isStaffUser } from 'src/services/permission/permission.helpers';
import {
  isPortalSyncNotConfiguredError,
  useGetUserJourneyQuery,
} from 'src/features/user-journey/endpoints/user-journey.endpoints';
import { EventLegend } from 'src/features/user-journey/components/event-legend';
import { JourneyMetricCards } from 'src/features/user-journey/components/journey-metric-cards';
import { NotAuthorisedState } from 'src/features/user-journey/components/not-authorised-state';
import { SessionList } from 'src/features/user-journey/components/session-card';
import { UserJourneyHeader } from 'src/features/user-journey/components/user-journey-header';
import { normaliseFlags } from 'src/features/user-journey/utils/user-journey.utils';
import { HIGH_GENERATIONS_PER_DOWNLOAD_FLAG } from 'src/features/user-journey/types/user-journey.types';

function buildBackHref(searchParams: URLSearchParams): string {
  const backParams = new URLSearchParams();
  const from = searchParams.get('from');
  const to = searchParams.get('to');
  const workspaceId = searchParams.get('workspace_id');

  if (from) backParams.set('from', from);
  if (to) backParams.set('to', to);
  if (workspaceId) backParams.set('workspace_id', workspaceId);

  return `/operations/usage-insights${backParams.toString() ? `?${backParams.toString()}` : ''}`;
}

const UserJourneyPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const permissions = useSelector((state: ReduxState) => state.userState.permissions);
  const isStaff = isStaffUser(permissions);
  const skipJourneyQuery = !isStaff || !userId;

  useSetPageTitle('User journey');

  const { data, isLoading, isError, error } = useGetUserJourneyQuery(userId ?? '', {
    skip: skipJourneyQuery,
  });

  const backHref = buildBackHref(searchParams);

  if (!isStaff) {
    return (
      <AppLayout activePage="/operations">
        <div className="h-[calc(100vh-8rem)] overflow-y-auto p-6">
          <NotAuthorisedState />
        </div>
      </AppLayout>
    );
  }

  if (!userId) {
    return (
      <AppLayout activePage="/operations">
        <div className="h-[calc(100vh-8rem)] overflow-y-auto p-6">
          <PortalDsAlert variant="danger" title="Missing user">
            A user id is required to view this journey.
          </PortalDsAlert>
        </div>
      </AppLayout>
    );
  }

  if (isLoading) {
    return (
      <AppLayout activePage="/operations">
        <div className="flex h-[calc(100vh-8rem)] items-center justify-center text-[#5E6278]">
          <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
        </div>
      </AppLayout>
    );
  }

  if (isPortalSyncNotConfiguredError(error)) {
    return (
      <AppLayout activePage="/operations">
        <div className="h-[calc(100vh-8rem)] overflow-y-auto p-6">
          <Link to={backHref} className="text-sm font-medium text-[#2563EB] hover:underline">
            ← Back to usage insights
          </Link>
          <div className="mt-8">
            <PortalDsAlert variant="warning" title="Portal sync not configured">
              User journey data is unavailable because portal sync is not configured.
            </PortalDsAlert>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (isError || !data) {
    return (
      <AppLayout activePage="/operations">
        <div className="h-[calc(100vh-8rem)] overflow-y-auto p-6">
          <Link to={backHref} className="text-sm font-medium text-[#2563EB] hover:underline">
            ← Back to usage insights
          </Link>
          <div className="mt-8">
            <PortalDsAlert variant="danger" title="Unable to load user journey">
              The journey view could not be loaded. The user may not exist or you may not have access.
            </PortalDsAlert>
          </div>
        </div>
      </AppLayout>
    );
  }

  const flags = normaliseFlags(data.flags);
  const showGenerationsWarning = flags.some(
    (flag) => flag.code === HIGH_GENERATIONS_PER_DOWNLOAD_FLAG,
  );

  return (
    <AppLayout activePage="/operations">
      <div className="h-[calc(100vh-8rem)] overflow-y-auto p-6">
        <Link to={backHref} className="text-sm font-medium text-[#2563EB] hover:underline">
          ← Back to usage insights
        </Link>

        <div className={cn(portalDs.page, 'mt-6')}>
          <UserJourneyHeader journey={data} />

          <JourneyMetricCards
            metrics={data.metrics}
            showGenerationsWarning={showGenerationsWarning}
          />

          <section>
            <PortalDsSectionHeader
              title="Sessions"
              description="Newest first. Expand a session to see the full ordered event list."
            />
            <EventLegend className="mb-4" />
            <SessionList userId={userId} sessions={data.sessions} />
          </section>
        </div>
      </div>
    </AppLayout>
  );
};

export default UserJourneyPage;
